/**
 * Prefetch storage: save/load prefetched products, categories, subcategories per store.
 * Uses IndexedDB (large quota, works in Safari/iOS) with fallback and migration from localStorage.
 */

import { enrichPrefetchSnapshot } from "@/utils/sharedBarcodeCount";

const PREFIX = "reshet_prefetch_";
const DB_NAME = "reshet_prefetch_db";
const DB_VERSION = 1;
const STORE_NAME = "prefetch";
/** גרסת מגוון — שינוי מסנן (ארכיון/chain) מבטל מטמון ישן */
export const CATALOG_PREFETCH_VERSION = 2;
/** גרסת כמות קבלה אחרונה — מבטל מטמון בלי lastReceiptQtyByProductId אמיתי */
export const LAST_RECEIPT_QTY_VERSION = 2;

export function getPrefetchKey(storeId) {
  return `${PREFIX}${storeId}`;
}

function openDB() {
  if (typeof indexedDB === "undefined") return Promise.reject(new Error("IndexedDB not available"));
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE_NAME)) {
        req.result.createObjectStore(STORE_NAME, { keyPath: "storeId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** @deprecated השתמשו ב-isProductOrderable — נשמר לתאימות */
export function isArchivedProduct(p) {
  if (!p || typeof p !== "object") return false;
  return !!(p.is_archived || p.isArchived);
}

function normalizeHomeSnapshot(snap) {
  if (!snap || typeof snap !== "object") return null;
  return {
    promotions: Array.isArray(snap.promotions) ? snap.promotions : [],
    orders: Array.isArray(snap.orders) ? snap.orders : [],
    orderedProductIds: Array.isArray(snap.orderedProductIds) ? snap.orderedProductIds : [],
    suppliers: Array.isArray(snap.suppliers) ? snap.suppliers : [],
    mustOrderTasks: Array.isArray(snap.mustOrderTasks) ? snap.mustOrderTasks : [],
    dailyInventoryCounts: Array.isArray(snap.dailyInventoryCounts) ? snap.dailyInventoryCounts : [],
    fetchedAt: Number(snap.fetchedAt) || Date.now(),
  };
}

function normalizePayload(storeId, payload) {
  return {
    storeId,
    timestamp: Date.now(),
    products: payload.products || [],
    categories: payload.categories || [],
    subcategories: payload.subcategories || [],
    subsubcategories: payload.subsubcategories || [],
    store: payload.store || null,
    companies: payload.companies || [],
    productsByCompany: payload.productsByCompany || {},
    chain: payload.chain && typeof payload.chain === "object" ? payload.chain : null,
    lastPurchaseByProductId:
      payload.lastPurchaseByProductId && typeof payload.lastPurchaseByProductId === "object"
        ? payload.lastPurchaseByProductId
        : {},
    lastReceiptQtyByProductId:
      payload.lastReceiptQtyByProductId && typeof payload.lastReceiptQtyByProductId === "object"
        ? payload.lastReceiptQtyByProductId
        : {},
    lastReceiptQtyVersion: Number(payload.lastReceiptQtyVersion) || 0,
    lastPurchasesPrefetched:
      !!payload.lastPurchasesPrefetched &&
      Number(payload.lastReceiptQtyVersion) === LAST_RECEIPT_QTY_VERSION,
    salesStatsPrefetched: !!payload.salesStatsPrefetched,
    salesStatsPeriodVersion: payload.salesStatsPeriodVersion ?? null,
    homeSnapshot: normalizeHomeSnapshot(payload.homeSnapshot),
    catalogVersion: CATALOG_PREFETCH_VERSION,
  };
}

function normalizeLoaded(data, expectedStoreId = null) {
  if (!data?.products || !Array.isArray(data.products)) return null;
  const expected = expectedStoreId ? String(expectedStoreId) : "";
  const loadedStoreId = data.store?._id ?? data.store?.id ?? null;
  if (expected && String(loadedStoreId || "") !== expected) {
    return null;
  }
  if ((data.catalogVersion ?? 1) < CATALOG_PREFETCH_VERSION) {
    return null;
  }
  return enrichPrefetchSnapshot({
    storeId: data.storeId,
    timestamp: data.timestamp || 0,
    products: data.products,
    categories: Array.isArray(data.categories) ? data.categories : [],
    subcategories: Array.isArray(data.subcategories) ? data.subcategories : [],
    subsubcategories: Array.isArray(data.subsubcategories) ? data.subsubcategories : [],
    store: data.store || null,
    companies: Array.isArray(data.companies) ? data.companies : [],
    productsByCompany: data.productsByCompany && typeof data.productsByCompany === "object" ? data.productsByCompany : {},
    chain: data.chain && typeof data.chain === "object" ? data.chain : null,
    lastPurchaseByProductId:
      data.lastPurchaseByProductId && typeof data.lastPurchaseByProductId === "object"
        ? data.lastPurchaseByProductId
        : {},
    lastReceiptQtyByProductId:
      data.lastReceiptQtyByProductId && typeof data.lastReceiptQtyByProductId === "object"
        ? data.lastReceiptQtyByProductId
        : {},
    lastReceiptQtyVersion: Number(data.lastReceiptQtyVersion) || 0,
    lastPurchasesPrefetched:
      !!data.lastPurchasesPrefetched &&
      Number(data.lastReceiptQtyVersion) === LAST_RECEIPT_QTY_VERSION,
    salesStatsPrefetched: !!data.salesStatsPrefetched,
    salesStatsPeriodVersion: data.salesStatsPeriodVersion ?? null,
    homeSnapshot: normalizeHomeSnapshot(data.homeSnapshot),
    catalogVersion: data.catalogVersion ?? CATALOG_PREFETCH_VERSION,
  });
}

/**
 * Load prefetched data (async). Prefers IndexedDB; migrates from localStorage once if present.
 * @param {string} storeId
 * @returns {Promise<{ storeId: string, timestamp: number, products: any[], categories: any[], subcategories: any[], store: any, companies: any[], productsByCompany: Record<string, any[]> } | null>}
 */
export function loadPrefetched(storeId) {
  if (!storeId) return Promise.resolve(null);

  return openDB()
    .then((db) => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(storeId);
        req.onsuccess = () => {
          const data = req.result ? normalizeLoaded(req.result, storeId) : null;
          if (data) {
            resolve(data);
            return;
          }
          // Migrate from legacy localStorage if present
          try {
            const raw = localStorage.getItem(getPrefetchKey(storeId));
            if (!raw) {
              resolve(null);
              return;
            }
            const parsed = JSON.parse(raw);
            const migrated = normalizeLoaded(parsed, storeId);
            if (migrated) {
              // Return migrated data; next "התחל הזמנה" will save to IndexedDB (avoids TDZ/circular call issues)
              resolve(migrated);
            } else {
              resolve(null);
            }
          } catch {
            resolve(null);
          }
        };
        req.onerror = () => reject(req.error);
      });
    })
    .catch(() => {
      // IndexedDB unavailable (e.g. private mode in some browsers): try localStorage
      try {
        const raw = localStorage.getItem(getPrefetchKey(storeId));
        if (!raw) return null;
        return Promise.resolve(normalizeLoaded(JSON.parse(raw), storeId));
      } catch {
        return Promise.resolve(null);
      }
    });
}

/**
 * Save prefetched data (async). Uses IndexedDB; throws on failure so UI can show error.
 * @param {string} storeId
 * @param {{ products: any[], categories: any[], subcategories: any[], store: any, companies?: any[], productsByCompany?: Record<string, any[]> }} payload
 * @returns {Promise<void>}
 */
export function savePrefetched(storeId, payload) {
  if (!storeId) return Promise.resolve();

  const data = normalizePayload(storeId, payload);
  // Build fallback payload synchronously so async .catch() doesn't touch `data` (avoids TDZ in some runtimes)
  const reducedForFallback = {
    ...data,
    companies: (data.companies || []).map((c) => {
      const { logo_data_url, ...rest } = c;
      return rest;
    }),
    chain: data.chain && typeof data.chain === "object" ? (() => {
      const { logo_data_url, ...rest } = data.chain;
      return rest;
    })() : null,
  };

  return openDB()
    .then((db) => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.put(data);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
        tx.onerror = () => reject(tx.error);
      });
    })
    .catch(() => {
      // Fallback: try localStorage with reduced payload (no base64 logos) to stay under quota
      try {
        localStorage.setItem(getPrefetchKey(storeId), JSON.stringify(reducedForFallback));
        return;
      } catch (e) {
        console.warn("prefetchStorage.savePrefetched failed", e);
        throw new Error("לא ניתן לשמור את הקטלוג במכשיר (מגבלת אחסון). נסה בדפדפן אחר או נקה נתוני אתר.");
      }
    });
}

/**
 * Clear prefetched data for a store (async).
 * @param {string} storeId
 */
export function clearPrefetched(storeId) {
  if (!storeId) return Promise.resolve();

  return openDB()
    .then((db) => {
      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.delete(storeId);
        req.onsuccess = () => {
          try {
            localStorage.removeItem(getPrefetchKey(storeId));
          } catch (_) {}
          resolve();
        };
        req.onerror = () => reject(req.error);
      });
    })
    .catch(() => {
      try {
        localStorage.removeItem(getPrefetchKey(storeId));
      } catch (_) {}
      return Promise.resolve();
    });
}

/** Cache API name for prefetched images */
export const PREFETCH_IMAGE_CACHE = "reshet-prefetch-images";

const PRODUCTION_API = "https://api.reshet.app/api";
const DEV_API = "https://dev.api.reshet.app";

/** בסביבת פרודקשן (דומיין לא localhost) – תמיד להשתמש ב-API של הפרודקשן, גם אם ה-build לא קיבל VITE_API_URL */
function getEffectiveApiBase() {
  const fromEnv = typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL;
  if (typeof window !== "undefined" && window.location?.hostname) {
    const isLocalhost = /^localhost$|^127\.0\.0\.1$/i.test(window.location.hostname);
    if (!isLocalhost) return (fromEnv && !fromEnv.includes("localhost") ? fromEnv : null) || PRODUCTION_API;
  }
  return fromEnv || DEV_API;
}

/**
 * Same URL we use for prefetch cache – use this for every img/src so offline serves from cache.
 * When forPrefetch=true we add skipOptimize=1 so backend streams S3 without sharp (~30s for 1000 images).
 * @param {string} rawUrl - S3 or external image URL
 * @param {boolean} [forPrefetch] - true = URL for prefetch (fast path); same URL must be used when displaying prefetched data
 * @returns {string|null} proxy URL (same-origin) or null
 */
export function getProxyImageUrl(rawUrl, forPrefetch = false) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const u = rawUrl.trim();
  if (!u) return null;
  const apiBase = getEffectiveApiBase();
  if (u.startsWith(apiBase)) return u;
  const base = `${apiBase}/products/optimized-image?imageUrl=${encodeURIComponent(u)}`;
  return forPrefetch ? `${base}&skipOptimize=1` : base;
}

/**
 * Check if Cache API is available (e.g. secure context).
 * @returns {boolean}
 */
export function isImageCacheAvailable() {
  return typeof caches !== "undefined";
}
